import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { assetsAPI, companiesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate, getStatusColor, getStatusLabel, getCategoryColor, getCategoryLabel } from '../lib/utils';
import { Plus, Search, Server, ChevronRight, Calendar, Shield, Building2 } from 'lucide-react';

export function Assets() {
    const { isAdmin } = useAuth();
    const [searchParams] = useSearchParams();
    const [assets, setAssets] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
    const [companyFilter, setCompanyFilter] = useState('all');

    useEffect(() => {
        loadData();
    }, [categoryFilter, statusFilter, companyFilter]);

    const loadData = async () => {
        try {
            const params = {};
            if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
            if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
            if (companyFilter && companyFilter !== 'all') params.company_id = companyFilter;

            const [assetsRes, companiesRes] = await Promise.all([
                assetsAPI.getAll(params),
                isAdmin ? companiesAPI.getAll() : Promise.resolve({ data: [] })
            ]);

            setAssets(assetsRes.data);
            setCompanies(companiesRes.data);
        } catch (error) {
            console.error('Load error:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredAssets = assets.filter(asset =>
        asset.name.toLowerCase().includes(search.toLowerCase()) ||
        asset.serial_number.toLowerCase().includes(search.toLowerCase()) ||
        asset.company_name?.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-48" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="assets-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Varlık Yönetimi</h1>
                    <p className="text-slate-500 text-sm mt-1">Cihaz ve yazılım envanterinizi yönetin</p>
                </div>
                {isAdmin && (
                    <Link to="/assets/new">
                        <Button className="bg-orwion-green hover:bg-orwion-green-hover" data-testid="create-asset-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Yeni Varlık
                        </Button>
                    </Link>
                )}
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Varlık ara..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                                <SelectValue placeholder="Kategori" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                                <SelectItem value="network">Ağ Cihazı</SelectItem>
                                <SelectItem value="software">Yazılım Lisansı</SelectItem>
                                <SelectItem value="low_voltage">Zayıf Akım</SelectItem>
                                <SelectItem value="other">Diğer</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                                <SelectValue placeholder="Garanti Durumu" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tüm Durumlar</SelectItem>
                                <SelectItem value="active">Aktif</SelectItem>
                                <SelectItem value="warranty_expiring">Garanti Bitiyor</SelectItem>
                                <SelectItem value="warranty_expired">Garanti Bitti</SelectItem>
                            </SelectContent>
                        </Select>
                        {isAdmin && (
                            <Select value={companyFilter} onValueChange={setCompanyFilter}>
                                <SelectTrigger className="w-full sm:w-48" data-testid="company-filter">
                                    <SelectValue placeholder="Şirket" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tüm Şirketler</SelectItem>
                                    {companies.map(company => (
                                        <SelectItem key={company.id} value={company.id}>
                                            {company.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Assets Grid */}
            {filteredAssets.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredAssets.map((asset) => (
                        <Link key={asset.id} to={`/assets/${asset.id}`} data-testid={`asset-card-${asset.id}`}>
                            <Card className="hover:shadow-md transition-all cursor-pointer h-full group">
                                <CardContent className="p-5">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="w-12 h-12 rounded-xl bg-orwion-green/10 flex items-center justify-center group-hover:bg-orwion-green/20 transition-colors">
                                            <Server className="w-6 h-6 text-orwion-green" />
                                        </div>
                                        <Badge className={getStatusColor(asset.status)}>
                                            {getStatusLabel(asset.status)}
                                        </Badge>
                                    </div>

                                    <h3 className="font-semibold text-slate-800 mb-1 truncate">{asset.name}</h3>
                                    <p className="text-sm text-slate-500 mb-3 font-mono">{asset.serial_number}</p>

                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center justify-between text-slate-500">
                                            <span className="flex items-center gap-1">
                                                <Building2 className="w-3 h-3" />
                                                {asset.company_name}
                                            </span>
                                            <Badge variant="outline" className={getCategoryColor(asset.category)}>
                                                {getCategoryLabel(asset.category)}
                                            </Badge>
                                        </div>

                                        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                                            <span className="flex items-center gap-1 text-slate-400">
                                                <Calendar className="w-3 h-3" />
                                                Kurulum
                                            </span>
                                            <span className="text-slate-600">{formatDate(asset.installation_date)}</span>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <span className="flex items-center gap-1 text-slate-400">
                                                <Shield className="w-3 h-3" />
                                                Garanti Bitiş
                                            </span>
                                            <span className={`font-medium ${asset.status === 'warranty_expired' ? 'text-red-600' : asset.status === 'warranty_expiring' ? 'text-amber-600' : 'text-slate-600'}`}>
                                                {formatDate(asset.warranty_end_date)}
                                            </span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="p-12 text-center">
                        <Server className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <h3 className="font-semibold text-slate-600 mb-1">Varlık Bulunamadı</h3>
                        <p className="text-sm text-slate-400 mb-4">
                            {search ? 'Arama kriterlerine uygun varlık bulunamadı.' : 'Henüz varlık eklenmemiş.'}
                        </p>
                        {isAdmin && (
                            <Link to="/assets/new">
                                <Button className="bg-orwion-green hover:bg-orwion-green-hover" data-testid="empty-create-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    İlk Varlığı Ekle
                                </Button>
                            </Link>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

export default Assets;
